import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../../config/api';
import { formatPledgeAmount } from '../../utils/formatPledgeAmount';

const PledgeManagement = () => {
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningReminders, setRunningReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [currency, setCurrency] = useState('KES');

  const getTotalsByCurrency = () => {
    const totals = {};
    pledges.forEach((p) => {
      const cur = p.currency || currency;
      const amt = parseFloat(p.amount) || 0;
      const isRec = Number(p.is_received) === 1;

      if (!totals[cur]) {
        totals[cur] = { pending: 0, received: 0, total: 0 };
      }

      totals[cur].total += amt;
      if (isRec) {
        totals[cur].received += amt;
      } else {
        totals[cur].pending += amt;
      }
    });
    return totals;
  };

  const totalsByCurrency = getTotalsByCurrency();

  useEffect(() => {
    fetchPledges();
    fetchCurrency();
  }, []);

  const fetchCurrency = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.GET_SETTINGS);
      if (res.data.success && res.data.settings?.pledge_currency) {
        setCurrency(res.data.settings.pledge_currency);
      }
    } catch {
      /* keep KES default */
    }
  };

  const fetchPledges = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.ADMIN_PLEDGES);
      if (res.data.success) setPledges(res.data.pledges);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const runDueReminders = async () => {
    setRunningReminders(true);
    setReminderResult(null);
    try {
      const res = await axios.post(API_ENDPOINTS.ADMIN_RUN_PLEDGE_REMINDERS);
      setReminderResult(res.data);
      fetchPledges();
    } catch (e) {
      setReminderResult({
        success: false,
        message: e.response?.data?.message || 'Failed to run reminders',
      });
    } finally {
      setRunningReminders(false);
    }
  };

  const toggleReceived = async (pledge) => {
    try {
      await axios.put(API_ENDPOINTS.ADMIN_PLEDGES, {
        id: pledge.id,
        is_received: !Number(pledge.is_received),
      });
      fetchPledges();
    } catch {
      alert('Could not update pledge');
    }
  };

  if (loading) return <div className="loading">Loading pledges...</div>;

  return (
    <div className="pledge-management">
      <h1>Cash Pledges</h1>
      <p className="subtitle">Pledges submitted via the public pledge card form</p>
      
      {/* Pledges Summary Cards */}
      <div className="pledges-summary-cards" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px', marginTop: '16px' }}>
        {Object.entries(totalsByCurrency).map(([cur, data]) => (
          <div key={cur} className="pledge-summary-card" style={{ flex: '1 1 200px', minWidth: '200px', padding: '16px', borderRadius: '12px', background: '#fff', border: '1px solid #eaeaea', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', color: '#666', borderBottom: '1px solid #eee', paddingBottom: '6px', fontWeight: 'bold' }}>{cur} Pledges</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.95rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888' }}>Total Pledged:</span>
                <strong style={{ color: '#333' }}>{formatPledgeAmount(data.total, cur)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#2e7d32' }}>Received:</span>
                <strong style={{ color: '#2e7d32' }}>{formatPledgeAmount(data.received, cur)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#d32f2f' }}>Pending:</span>
                <strong style={{ color: '#d32f2f' }}>{formatPledgeAmount(data.pending, cur)}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pledge-toolbar">
        <button
          type="button"
          className="btn-primary"
          onClick={runDueReminders}
          disabled={runningReminders}
        >
          {runningReminders ? 'Sending reminders…' : 'Send due reminders now'}
        </button>
        <span className="pledge-toolbar-hint">
          Sends email to guests whose reminder date has passed and payment is still pending.
        </span>
      </div>
      {reminderResult && (
        <div className={`pledge-run-result ${reminderResult.success ? 'ok' : 'warn'}`}>
          {reminderResult.message}
          {reminderResult.errors?.length > 0 && (
            <ul>
              {reminderResult.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Amount</th>
              <th>Reminder</th>
              <th>Reminder sent</th>
              <th>Received</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {pledges.length === 0 ? (
              <tr>
                <td colSpan={9}>No pledges yet</td>
              </tr>
            ) : (
              pledges.map((p) => (
                <tr key={p.id}>
                  <td>{p.pledge_reference || '—'}</td>
                  <td>{p.guest_name}</td>
                  <td>{p.guest_email}</td>
                  <td>{p.guest_phone}</td>
                  <td>{formatPledgeAmount(p.amount, p.currency || currency)}</td>
                  <td>{p.reminder_date || '—'}</td>
                  <td>
                    {Number(p.reminder_sent) === 1 ? (
                      <span className="reminder-sent yes" title={p.reminder_sent_at || ''}>
                        Sent
                      </span>
                    ) : (
                      <span className="reminder-sent no">Pending</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`status-btn ${Number(p.is_received) ? 'received' : ''}`}
                      onClick={() => toggleReceived(p)}
                    >
                      {Number(p.is_received) ? 'Received' : 'Pending'}
                    </button>
                  </td>
                  <td>{new Date(p.pledged_at).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PledgeManagement;
